import { CONTEXT_TABS_SNAPSHOT_VERSION } from '@web/hooks';

import { isContextTabWorkspaceUrl } from './contextTabRoutes';

const STORAGE_PREFIX = 'netnet:context-tabs:app';

export function createContextTabsStorageKey(
  userId: string,
  workspaceId: string,
) {
  return `${STORAGE_PREFIX}:${userId}:${workspaceId}:v${CONTEXT_TABS_SNAPSHOT_VERSION}`;
}

export function loadContextTabsSnapshot(
  userId: string,
  workspaceId: string,
  workspaceSlug?: string,
): unknown | null {
  if (typeof window === 'undefined') return null;
  const key = createContextTabsStorageKey(userId, workspaceId);

  try {
    const raw = window.localStorage.getItem(key);
    const snapshot: unknown = raw ? JSON.parse(raw) : null;
    if (!workspaceSlug || !snapshot || typeof snapshot !== 'object') {
      return snapshot;
    }

    const candidate = snapshot as {
      activeTabId?: unknown;
      tabs?: unknown;
    };
    if (!Array.isArray(candidate.tabs)) return snapshot;
    const tabs = candidate.tabs.filter((tab) => {
      if (!tab || typeof tab !== 'object') return false;
      const persistedTab = tab as { url?: unknown; history?: unknown };
      return (
        typeof persistedTab.url === 'string' &&
        isContextTabWorkspaceUrl(persistedTab.url, workspaceSlug) &&
        Array.isArray(persistedTab.history) &&
        persistedTab.history.every(
          (entry) =>
            Boolean(entry) &&
            typeof entry === 'object' &&
            'url' in entry &&
            typeof entry.url === 'string' &&
            isContextTabWorkspaceUrl(entry.url, workspaceSlug),
        )
      );
    });
    if (!tabs.length) return null;
    const hasActiveTab = tabs.some(
      (tab) =>
        tab &&
        typeof tab === 'object' &&
        'id' in tab &&
        tab.id === candidate.activeTabId,
    );
    const firstTab = tabs[0] as { id?: unknown };
    return {
      ...snapshot,
      activeTabId: hasActiveTab ? candidate.activeTabId : firstTab.id,
      tabs,
    };
  } catch {
    window.localStorage.removeItem(key);
    return null;
  }
}

export function saveContextTabsSnapshot(
  userId: string,
  workspaceId: string,
  snapshot: unknown,
) {
  if (typeof window === 'undefined') return false;

  try {
    window.localStorage.setItem(
      createContextTabsStorageKey(userId, workspaceId),
      JSON.stringify(snapshot),
    );
    return true;
  } catch {
    return false;
  }
}

export function getSavedActiveContextTabUrl(
  userId: string,
  workspaceId: string,
  workspaceSlug: string,
) {
  const snapshot = loadContextTabsSnapshot(userId, workspaceId);
  if (!snapshot || typeof snapshot !== 'object') return null;
  const candidate = snapshot as {
    activeTabId?: unknown;
    tabs?: unknown;
  };
  if (
    typeof candidate.activeTabId !== 'string' ||
    !Array.isArray(candidate.tabs)
  ) {
    return null;
  }
  const active = candidate.tabs.find(
    (tab): tab is { id: string; url: string } =>
      Boolean(
        tab &&
          typeof tab === 'object' &&
          'id' in tab &&
          tab.id === candidate.activeTabId &&
          'url' in tab &&
          typeof tab.url === 'string',
      ),
  );
  return active && isContextTabWorkspaceUrl(active.url, workspaceSlug)
    ? active.url
    : null;
}
